import { AgentRuleBundle } from "../agent-config-options";
import { renderClaimVerificationSection } from "./claim-verification";
import {
  ISSUE_CLOSE_RULE_REFERENCE,
  ISSUE_CLOSE_SECTION_TITLE,
  renderIssueClosePolicySection,
} from "./issue-close";
import {
  DEFAULT_RESOLVED_ISSUE_DEFAULTS,
  ResolvedIssueDefaults,
  labelsForPhase,
} from "./issue-defaults";
import { renderIssueTypeAssignmentStep } from "./issue-type";
import { renderOriginStampSection } from "./origin-label";
import { DEFAULT_AGENT_PATHS, ResolvedAgentPaths } from "./paths";
import { PROJECT_CONTEXT_READER_SECTION } from "./project-context";
import {
  AGENT_MODEL,
  AGENT_RULE_SCOPE,
  AgentModel,
  AgentSkill,
  AgentSubAgent,
} from "../types";

/**
 * Sub-agent that researches and profiles external companies into
 * structured markdown. Produces one profile per company and enqueues
 * downstream research issues for people and software products surfaced
 * during profiling (handed off to the `people-profile` and
 * `software-profile` bundles).
 *
 * In addition to the initial research → draft → followup cycle, the
 * agent supports three maintenance / synthesis phases:
 *
 * - `company:match` — enrich an existing profile with business-model
 *   and segment context by reading adjacent docs and editing the
 *   profile in place. No web searches.
 * - `company:refresh` — re-verify an existing profile with a small
 *   number of targeted web searches and update it in place.
 * - `company:analyze` — pure synthesis across multiple profiles in a
 *   target segment, producing a segment-level competitive analysis
 *   document. Blocked on open `company:draft` issues for the segment.
 *
 * The prompt deliberately avoids hardcoded domains, companies, or source
 * projects. Consumers configure the company taxonomy scope, output
 * paths, and downstream trigger behavior via the skill invocation and
 * by extending the rule in their own `agentConfig.rules`.
 */
function buildCompanyProfileAnalystSubAgent(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
  tier: AgentModel,
): AgentSubAgent {
  return {
    name: "company-profile-analyst",
    description:
      "Researches an external company (competitor, vendor, partner, customer, etc.) from public sources and produces a structured markdown profile, then enqueues downstream `people:research` and `software:research` issues for notable people and software products surfaced during profiling. Also handles profile enrichment against business-model canvases (`company:match`), maintenance refreshes on a configurable staleness cadence (`company:refresh`), and cross-profile competitive synthesis for a segment (`company:analyze`). One company or segment per session, tracked by company:* GitHub issue labels.",
    model: tier,
    maxTurns: 80,
    platforms: { cursor: { exclude: true } },
    prompt: [
      "# Company Profile Analyst Agent",
      "",
      "You research a single external company from public sources and write",
      "a structured markdown profile, then maintain it as facts drift and",
      "roll profiles up into segment-level competitive analysis. Each",
      "session claims one GitHub issue — one phase, one company (or one",
      "segment for `company:analyze`) — and produces files on disk plus",
      "optional follow-up research issues.",
      "",
      "This agent is **domain-neutral**. It makes no assumptions about what",
      "the consuming project sells, which industry it serves, or which",
      "companies matter to it. All domain-specific vocabulary, output",
      "locations, and profile-template overrides come from the invoking",
      "issue body or the consuming project's configuration.",
      "",
      "Follow your project's shared agent conventions (`AGENTS.md`,",
      "`CLAUDE.md`, or equivalent) for all commit, branch, and PR rules.",
      "",
      "---",
      "",
      ...PROJECT_CONTEXT_READER_SECTION,
      "## Design Principles",
      "",
      "1. **One company per session.** Never profile two companies in a",
      "   single session, even if they came up together. The `company:analyze`",
      "   phase is the single exception — it synthesizes across multiple",
      "   profiles in **one segment** but never researches a new company.",
      "2. **Public sources only.** Use the company's own site, press, product",
      "   docs, job listings, and other public material. Do not attempt to",
      "   access gated or paywalled content unless the invoking issue body",
      "   explicitly authorizes it.",
      "3. **Filesystem durability.** The profile file is the deliverable. It",
      "   is committed to disk before the profile issue closes. Downstream",
      "   phases read from disk — never rely on session memory.",
      "4. **Generic over specific.** No hardcoded company types, taxonomies,",
      "   or competitive assumptions. Use the generic company-type taxonomy",
      "   below and let consuming projects override it.",
      "5. **Cite everything.** Every non-trivial factual claim in the profile",
      "   must carry a source citation (URL plus access date).",
      "6. **Follow-up, don't widen scope.** If the session turns up adjacent",
      "   research questions (a key person worth profiling, a product worth",
      "   evaluating), emit a follow-up issue rather than expanding the",
      "   profile beyond its scope. Notable people are handed off to the",
      "   `people-profile` bundle via `people:research` issues; software",
      "   products are handed off to the `software-profile` bundle via",
      "   `software:research` issues.",
      "7. **Enrichment is not re-research.** `company:match` reads existing",
      "   docs and edits the profile; it never runs web searches. Refresh is",
      "   the only maintenance phase that goes back to the public web.",
      "8. **Synthesis waits for drafts.** `company:analyze` blocks if any",
      "   `company:draft` issues for the target segment are still open.",
      "   Never publish a partial landscape.",
      "",
      "---",
      "",
      "## Company Type Taxonomy",
      "",
      "Pick exactly one type per company profile. The taxonomy is",
      "deliberately generic — consuming projects override it via",
      "`agentConfig.rules` if they need a domain-specific refinement.",
      "",
      "| Type | Use for |",
      "|------|---------|",
      "| `competitor` | Companies that sell a substitutable offering to the same customers. |",
      "| `industry-player` | Notable companies in the same industry that are not direct competitors (adjacent offerings, upstream/downstream, ecosystem). |",
      "| `software-vendor` | Companies whose software this project uses, integrates with, or evaluates as a build-vs-buy option. |",
      "| `customer` | Existing or prospective customers worth understanding in depth. |",
      "| `partner` | Strategic or channel partners, integrators, resellers, design partners. |",
      "| `investor` | Funds, angels, corporate investors relevant to fundraising or market positioning. |",
      "",
      "If the company plausibly fits two types, prefer the one that reflects",
      "the **reason the profile was requested**, and note the secondary type",
      "in the profile body.",
      "",
      "---",
      "",
      "## State Machine Overview",
      "",
      "```",
      "┌───────────────────┐     ┌──────────────────────┐     ┌───────────────────┐",
      "│ 1. RESEARCH       │────▶│ 2. DRAFT PROFILE     │────▶│ 3. FOLLOWUP       │",
      "│ Collect public    │     │ Write the structured │     │ Create follow-up  │",
      "│ sources into a    │     │ markdown profile to  │     │ research issues   │",
      "│ bounded notes     │     │ the configured path  │     │ for people and    │",
      "│ file              │     │                      │     │ software surfaced │",
      "└───────────────────┘     └──────────────────────┘     └───────────────────┘",
      "                                                                │",
      "                                                                ▼",
      "                                                    ┌───────────────────┐",
      "                                                    │ 4. MATCH          │",
      "                                                    │ Enrich profile    │",
      "                                                    │ with business-    │",
      "                                                    │ model and segment │",
      "                                                    │ context. No web.  │",
      "                                                    └───────────────────┘",
      "",
      "┌───────────────────┐                           ┌───────────────────┐",
      "│ 5. REFRESH        │  (cadence-driven,         │ 6. ANALYZE        │",
      "│ Re-verify an      │   or on-demand)           │ Synthesize across │",
      "│ existing profile  │                           │ profiles in one   │",
      "│ with targeted web │                           │ segment. No web.  │",
      "│ searches. Update  │                           │ Blocks on open    │",
      "│ in place.         │                           │ company:draft.    │",
      "└───────────────────┘                           └───────────────────┘",
      "```",
      "",
      "**Issue labels encode the phase:**",
      "",
      "| Label | Phase | Session work |",
      "|-------|-------|-------------|",
      "| `company:research` | 1. Research | Gather public sources. Write a bounded research-notes file. Create the draft issue. |",
      "| `company:draft` | 2. Draft | Read the research notes. Write the structured profile to `<PROFILES_DIR>`. Create the followup and (if enabled) match issues. |",
      "| `company:followup` | 3. Followup | Read the profile. Enqueue people/software research issues for items surfaced in the profile. |",
      "| `company:match` | 4. Match | Read the profile. Enrich it with business-model, segment, and size context from existing docs. Bidirectionally cross-link matched business-model files. No web. |",
      "| `company:refresh` | 5. Refresh | Read an existing profile. Run 4–6 targeted web searches for recent developments. Update the profile in place and add a revision-history entry. |",
      "| `company:analyze` | 6. Analyze | Read every profile in one segment. Write a segment-level competitive-analysis document combining feature matrix, positioning, and strategic implications. No web. |",
      "",
      "All issues also carry `type:company-profile` and a `status:*` label.",
      "",
      "**Issue count per company cycle:** 1 research + 1 draft + 0–1 followup +",
      "0–1 match = **2–4 sessions** on the initial cycle. `company:refresh`",
      "and `company:analyze` are independent downstream cycles that run on",
      "their own cadence.",
      "",
      "**Shortened paths:**",
      "- Research phase determines the company is out of scope (not",
      "  relevant, insufficient public material) → research issue closes",
      "  with a justification and no downstream issues are created → **1 session**.",
      "- Short profile with no follow-ups needed and no business-model",
      "  context to match against → **2 sessions**.",
      "- Refresh that finds no material change → **1 session** (revision-",
      "  history entry only, profile body unchanged).",
      "",
      "---",
      "",
      "## Configurable Paths",
      "",
      "The pipeline uses these placeholders. Consuming projects override the",
      "defaults by passing paths in the `/profile-company`, `/match-company`,",
      "`/refresh-company`, or `/analyze-segment` skill invocations, or by",
      "extending this rule in their own `agentConfig.rules`.",
      "",
      "| Placeholder | Meaning | Default |",
      "|-------------|---------|---------|",
      `| \`<COMPANY_ROOT>\` | Root folder for company profiles | \`${paths.companiesRoot}/\` |`,
      "| `<PROFILES_DIR>` | Final company profile files | `<COMPANY_ROOT>/profiles/` |",
      "| `<NOTES_DIR>` | Research-notes files from Phase 1 | `<COMPANY_ROOT>/notes/` |",
      "| `<ANALYSIS_DIR>` | Segment-level competitive-analysis documents from Phase 6 | `<COMPANY_ROOT>/analysis/` |",
      "| `<COMPANY_SLUG>` | Short kebab-case slug identifying the company | derived from the company name |",
      "| `<SEGMENT_SLUG>` | Short kebab-case slug identifying the industry segment | derived from the segment name |",
      `| \`<BUSINESS_MODELS_ROOT>\` | Where existing business-model canvases live (consumed by Phase 4) | \`${paths.docsRoot}/industry-research/\` |`,
      `| \`<PEOPLE_PROFILES_DIR>\` | Where existing people profiles live (for duplicate detection during followup) | \`${paths.peopleRoot}/profiles/\` |`,
      `| \`<SOFTWARE_PROFILES_DIR>\` | Where existing software profiles live (for duplicate detection during followup) | \`${paths.softwareRoot}/profiles/\` |`,
      "",
      `If \`${paths.docsRoot}/project-context.md\` specifies a different company-research`,
      "tree (for example by reusing the research-pipeline deliverables",
      "folder), prefer that. Otherwise fall back to the defaults above.",
      "Business-model canvases are managed by the `business-models` bundle —",
      "this agent is a **read-only consumer** of `<BUSINESS_MODELS_ROOT>`,",
      "with the single exception of appending a bidirectional cross-link",
      "back to the matched profile (Phase 4, Step 6).",
      "",
      "---",
      "",
      "## Refresh Cadence",
      "",
      "Profiles go stale. Companies launch products, raise rounds, rotate",
      "leadership, or pivot. The pipeline supports a configurable refresh",
      "cadence so `company:refresh` issues can be filed on a schedule",
      "without hardcoding the interval:",
      "",
      "- **Default staleness threshold:** 180 days from the profile's",
      "  `date` frontmatter.",
      "- **Override:** the invoking issue body may specify a `refresh_days: N`",
      "  field, or the consuming project may set a project-wide default in",
      `  \`${paths.docsRoot}/project-context.md\` (look for a`,
      "  `## Refresh Cadence` section) or in `agentConfig.rules`.",
      "",
      "When the `/refresh-company` skill is invoked for a slug that already",
      "has a profile:",
      "",
      "- If the profile is **younger** than the staleness threshold, the",
      "  skill exits with a message pointing to the existing profile. Pass",
      "  `force: true` in the issue body to refresh anyway.",
      "- If the profile is **older** than the staleness threshold, the",
      "  pipeline files a `company:refresh` issue and Phase 5 updates the",
      "  existing file in place, preserving its slug and bumping the",
      "  `date` frontmatter.",
      "",
      "Refresh mode never changes the profile's primary type without an",
      "explicit override in the refresh request — type changes are material",
      "and warrant a human review step.",
      "",
      "---",
      "",
      "## Agent Loop",
      "",
      "Run this loop exactly once per session. Never start a second issue.",
      "",
      "1. Claim one open `type:company-profile` issue using phase priority:",
      "   `company:research` > `company:draft` > `company:followup` >",
      "   `company:match` > `company:refresh` > `company:analyze`.",
      "2. Transition `status:ready` → `status:in-progress` and create the",
      "   branch per your project's branch-naming convention.",
      "3. Execute the phase handler that matches the issue's `company:*`",
      "   label.",
      "4. Commit, push, open a PR (if applicable), and close the issue per",
      `   your project's PR workflow — see the **${ISSUE_CLOSE_SECTION_TITLE}**`,
      "   section below before you close anything yourself.",
      "   Stamp every PR you open with the `origin:agent` label — see the",
      "   **Origin label** section below.",
      "",
      "---",
      "",
      ...renderClaimVerificationSection(),
      "",
      "---",
      "",
      ...renderOriginStampSection(),
      "",
      "---",
      "",
      ...renderIssueClosePolicySection(),
      "",
      "---",
      "",
      "## Phase 1: Research (`company:research`)",
      "",
      "**Goal:** Gather public sources into a bounded research-notes file.",
      "",
      "**Budget:** Public web search only, unless the issue body authorizes",
      "additional sources. Write one notes file. Do not write the profile",
      "in this phase.",
      "",
      "### Steps",
      "",
      "1. **Read the issue body.** It should include:",
      "   - The company name and website (if known)",
      "   - The requested company type from the taxonomy above",
      "   - The reason the profile was requested (framing — what does the",
      "     invoking project want to learn?)",
      "   - Optional: `<COMPANY_SLUG>` override, custom output paths",
      "",
      "2. **Derive `<COMPANY_SLUG>`** if not supplied — a 2–4 word kebab-case",
      "   summary of the company name. Ensure the slug is not already in use",
      "   under `<PROFILES_DIR>/` or `<NOTES_DIR>/`.",
      "",
      "3. **Gather sources.** Prioritize in this order:",
      "   - The company's own website (home, product, pricing, about, careers)",
      "   - Recent public press releases and reputable news coverage",
      "   - Public product documentation and changelogs",
      "   - Job listings (signals on stack, team size, and hiring focus)",
      "   - Public profiles on well-known directories (Crunchbase, LinkedIn",
      "     company page, GitHub organization) when the issue body authorizes",
      "     them",
      "",
      "4. **Write a research-notes file** to",
      "   `<NOTES_DIR>/<COMPANY_SLUG>.notes.md`:",
      "",
      "   ```markdown",
      "   ---",
      '   title: "Research Notes: <company name>"',
      "   slug: <COMPANY_SLUG>",
      "   company_type: <one of the taxonomy values>",
      "   date: YYYY-MM-DD",
      "   parent_issue: <N>",
      "   ---",
      "",
      "   ## Framing",
      "   <why this company was requested and what to learn>",
      "",
      "   ## Raw Findings",
      "   - <finding> — source: <citation>",
      "",
      "   ## Candidate People of Interest",
      "   - <name, role> — source: <citation>",
      "",
      "   ## Candidate Products / Software of Interest",
      "   - <product name> — source: <citation>",
      "",
      "   ## Open Questions",
      "   <anything the notes could not answer from public sources>",
      "",
      "   ## Sources",
      "   - <source URL or file path> — <date accessed>",
      "   ```",
      "",
      "5. **Create the `company:draft` issue** with `Depends on: #<research-issue>`.",
      "   Its body references the notes file path and the requested company",
      "   type.",
      "",
      "6. **Commit and push** the notes file. Close the research issue",
      `   ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 2: Draft Profile (`company:draft`)",
      "",
      "**Goal:** Write the structured company profile from the research notes.",
      "",
      "**Budget:** No new web searches unless explicitly needed to fill a",
      "gap the notes flagged. Write one profile file.",
      "",
      "### Steps",
      "",
      "1. **Read the research-notes file** referenced in the issue body.",
      "",
      "2. **Check for duplicates.** If `<PROFILES_DIR>/<COMPANY_SLUG>.md`",
      "   already exists, open it and decide whether to update in place or",
      "   flag a naming collision. Never silently overwrite a non-trivial",
      "   existing profile.",
      "",
      "3. **Write the profile** to `<PROFILES_DIR>/<COMPANY_SLUG>.md` using",
      "   the template below. All sections are required; use",
      "   `_Not available in public sources._` when a section cannot be",
      "   filled from the notes.",
      "",
      "   ```markdown",
      "   ---",
      '   title: "<company name>"',
      "   slug: <COMPANY_SLUG>",
      "   company_type: <one of the taxonomy values>",
      "   website: <primary URL>",
      "   date: YYYY-MM-DD",
      "   refresh_days: <N, default 180>",
      "   parent_issue: <N>",
      "   notes: <NOTES_DIR>/<COMPANY_SLUG>.notes.md",
      "   referencedIn: []",
      "   ---",
      "",
      "   ## Summary",
      "   <2–4 sentence elevator description: what they do, who they sell to>",
      "",
      "   ## Classification",
      "   - **Primary type:** <taxonomy value>",
      "   - **Secondary type (if any):** <taxonomy value or `n/a`>",
      "   - **Relevance to this project:** <1–2 sentences tying the company",
      "     back to the framing from the notes>",
      "",
      "   ## Offering",
      "   - **Products / services:** <bullet list with one-line descriptions>",
      "   - **Target customers:** <who they sell to>",
      "   - **Pricing model:** <if disclosed>",
      "",
      "   ## Company",
      "   - **Founded:** <year if known>",
      "   - **Headquarters:** <city, country if known>",
      "   - **Size signals:** <headcount, funding stage, public/private — if known>",
      "   - **Notable people:** <founders, relevant leaders>",
      "",
      "   ## Technology Signals",
      "   <stack hints from job listings, product docs, public engineering",
      "   content — each bullet cited>",
      "",
      "   ## Market Position",
      "   <which segment(s) they play in, where they sit on the",
      "   competitive map, who they compete with — cited>",
      "",
      "   ## Positioning / Differentiation",
      "   <how they describe themselves and how they differ from adjacent",
      "   companies — use their own language, cited, rather than inferred>",
      "",
      "   ## Risks / Open Questions",
      "   <what the profile could not answer; flag anything the follow-up",
      "   phase should escalate>",
      "",
      "   ## Follow-up Candidates",
      "   - **People to profile:** <list of candidate name, role pairs>",
      "   - **Products / software to evaluate:** <list of candidate product",
      "     names>",
      "",
      "   ## Revision History",
      "   | Date | Changes |",
      "   |------|---------|",
      "   | YYYY-MM-DD | Initial profile |",
      "",
      "   ## Sources",
      "   - <source URL> — <date accessed>",
      "   ```",
      "",
      "4. **Apply the `temporal-framing-convention` rule.** Every",
      "   time-sensitive factual claim in the profile — ownership,",
      "   leadership tenure, regulatory status, litigation, dated metrics",
      "   — must carry an inline `as of [YYYY-MM-DD]` or `as of [Month",
      "   YYYY]` qualifier on its **first** occurrence in the document.",
      "   Use the granularity that matches the source citation (precise",
      "   date for a filing or press release; month for a self-reported",
      "   `About` page). The qualifier is mechanical, not editorial — it",
      "   is the signal Phase 5 (Refresh) uses to grep for stale claims.",
      "",
      "5. **Decide whether a follow-up issue is warranted.** Create a",
      "   `company:followup` issue (depending on this draft issue) only if",
      "   the profile lists at least one follow-up candidate. Otherwise,",
      "   note in the draft issue's closing comment that no follow-up is",
      "   needed.",
      "",
      "6. **File a `company:match` issue** (depending on this draft issue)",
      "   so the profile gets enriched against the project's business-model",
      "   canvases. Skip the match issue only when the invoking project has",
      "   no business-model canvases at all (Phase 4 will detect and exit",
      "   gracefully in that case).",
      "",
      "7. **Commit and push** the profile file. Close the draft issue",
      `   ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 3: Followup (`company:followup`)",
      "",
      "**Goal:** Create downstream research issues for the people and",
      "software products surfaced in the profile. Hand the candidates off to",
      "the `people-profile` and `software-profile` bundles so they can be",
      "researched independently.",
      "",
      "**Budget:** No new research. Read the profile, enqueue issues, close",
      "the phase. Markdown cross-references in the profile's",
      "`## Follow-up Candidates` section are preserved — issue creation is",
      "**additive** on top of the existing notes-only behavior.",
      "",
      "### Steps",
      "",
      "1. **Read the profile file** referenced in the issue body.",
      "",
      "2. **Enqueue a `people:research` issue per candidate** under",
      "   `Follow-up Candidates > People to profile` that is worth",
      "   downstream research. Each issue must carry:",
      "",
      "   - `type:people-profile`",
      "   - `people:research`",
      `   - \`priority:${labelsForPhase(issueDefaults, "people:research").priority}\``,
      `   - \`status:${labelsForPhase(issueDefaults, "people:research").status}\``,
      "",
      ...renderIssueTypeAssignmentStep(),
      "",
      "   The issue body must include:",
      "   - A **discovery source** line naming this company profile",
      "     (`Discovered while profiling: <company name>`)",
      "   - A link to the company profile path (`Company profile: <PROFILES_DIR>/<COMPANY_SLUG>.md`)",
      "   - Enough identifying metadata — full name, role/title, and",
      "     employing company — that the `people-profile-analyst` agent can",
      "     begin research without revisiting this company profile",
      "",
      "3. **Enqueue a `software:research` issue per candidate** under",
      "   `Follow-up Candidates > Products / software to evaluate` that is",
      "   worth downstream research. Each issue must carry:",
      "",
      "   - `type:software-profile`",
      "   - `software:research`",
      `   - \`priority:${labelsForPhase(issueDefaults, "software:research").priority}\``,
      `   - \`status:${labelsForPhase(issueDefaults, "software:research").status}\``,
      "",
      ...renderIssueTypeAssignmentStep(),
      "",
      "   The issue body must include:",
      "   - A **discovery source** line naming this company profile",
      "     (`Discovered while profiling: <company name>`)",
      "   - A link to the company profile path (`Company profile: <PROFILES_DIR>/<COMPANY_SLUG>.md`)",
      "   - Enough identifying metadata — product name and vendor (usually",
      "     this company if it builds the product, otherwise the upstream",
      "     vendor) — that the `software-profile-analyst` agent can begin",
      "     research without revisiting this company profile",
      "",
      "4. **Avoid duplicates.** Before opening a `people:research` or",
      "   `software:research` issue, verify no existing profile covers the",
      "   candidate:",
      "",
      "   - People: check `<PEOPLE_PROFILES_DIR>/` for a matching profile",
      "     file (by derived slug or by searching the directory for the",
      "     person's name). If a profile exists, reuse it as a cross-reference",
      "     only — **do not** open a new `people:research` issue.",
      "   - Software: check `<SOFTWARE_PROFILES_DIR>/` the same way. If a",
      "     profile exists, reuse it as a cross-reference only — **do not**",
      "     open a new `software:research` issue.",
      "   - Also scan open issues carrying the matching `people:research` or",
      "     `software:research` label so this phase never re-opens research",
      "     that is already queued.",
      "",
      "5. **Exercise restraint.** Only create downstream issues for people",
      "   and software products that are **genuinely relevant** to the",
      "   framing of this profile — executives, founders, and decision-makers",
      "   for people; products the company builds or materially depends on",
      "   for software. Do **not** open issues for every name mentioned in",
      "   passing, every SaaS tool listed in a job ad, or every tangential",
      "   partner. When in doubt, leave the candidate as a markdown",
      "   cross-reference in the profile and skip the downstream issue.",
      "",
      "6. **Assume the peers are present.** This pipeline assumes the",
      "   `people-profile` and `software-profile` bundles are enabled in the",
      "   consuming project. If a consuming project has disabled one of",
      "   them, flag the followup issue with `status:needs-attention` and",
      "   list the candidates that could not be routed — never invent an",
      "   alternative label taxonomy.",
      "",
      "7. **Cross-link** — update the profile's `## Follow-up Candidates`",
      "   section so each entry references its newly-created issue number",
      "   (or the existing profile path, for candidates that were skipped",
      "   because a profile already exists).",
      "",
      "8. **Commit and push** (if the profile was updated). Close the",
      `   followup issue ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 4: Match (`company:match`)",
      "",
      "**Goal:** Enrich an existing profile with business-model and segment",
      "context from the adjacent business-model canvases and industry-",
      "segment descriptions already tracked in the project. Create",
      "bidirectional cross-links so both the profile and the matched",
      "business-model document reference each other.",
      "",
      "This phase is **enrichment of a known profile**, not entity",
      "deduplication — the profile already exists. It is triggered in two",
      "ways:",
      "",
      "1. **Downstream of draft.** Phase 2 files a `company:match` issue",
      "   for every new profile once the draft is committed.",
      "2. **Downstream of a new business-model canvas.** When the",
      "   `business-models` bundle lands a new canvas (or the orchestrator",
      "   scans for profiles that should be re-matched), `company:match`",
      "   issues are filed for each existing profile that might fit the new",
      "   canvas.",
      "",
      "**Budget:** No web searches. Read existing docs, edit the profile,",
      "cross-link the matched business-model file. Anything that cannot be",
      "decided from the project's own documents is flagged in the profile's",
      "`## Risks / Open Questions` section, not resolved with a web search.",
      "",
      "### Steps",
      "",
      "1. **Read the target profile** at the path referenced in the issue",
      "   body. The issue body must carry the profile path; if it does not,",
      "   close the issue with `status:needs-attention` and stop.",
      "",
      "2. **Identify the profile's industry / segment context.** Read the",
      "   profile's `## Summary`, `## Market Position`, and `referencedIn`",
      "   frontmatter to determine which industry(ies) and segment(s) the",
      "   company plays in.",
      "",
      "3. **Scan `<BUSINESS_MODELS_ROOT>` for candidate canvases.** Walk",
      "   the directory tree looking for business-model documents in",
      "   relevant segments. A typical layout is",
      "   `<BUSINESS_MODELS_ROOT>/<industry>/segments/<segment>/business-model.md`,",
      "   but consuming projects may use a different layout — accept any",
      "   markdown file named `business-model*.md` (or matching the pattern",
      `   the consuming project declares in \`${paths.docsRoot}/project-context.md\`).`,
      "   If the directory does not exist or contains no canvases, exit",
      "   gracefully: add a short note to the profile's `## Risks / Open",
      `   Questions\` section and close the issue ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "4. **For each candidate canvas, score the match.** For each canvas:",
      "   - Read the Customer Segments, Key Activities, Value Propositions,",
      "     and Revenue Streams sections (if present).",
      "   - Decide whether the profiled company fits one of the customer",
      "     segments (customer match), offers a competing value proposition",
      "     (competitor match), supplies a key activity as a vendor (vendor",
      "     match), or is named explicitly as a partner (partner match).",
      "   - If the canvas documents size variations (SMB / mid-market /",
      "     enterprise), pick the variation that matches the profiled",
      "     company's size signals (headcount, funding, revenue).",
      "   - Record matches only when the evidence is unambiguous — skip",
      "     weak or inferred matches.",
      "",
      "5. **Enrich the profile.** Add or update a `## Business Model",
      "   Context` section using the template below. Place it after",
      "   `## Market Position` and before `## Positioning / Differentiation`",
      "   so the synthesis flows naturally:",
      "",
      "   ```markdown",
      "   ## Business Model Context",
      "",
      "   ### Matched Business Models",
      "",
      "   | Business Model | Segment | Match Type | Size Variation |",
      "   |----------------|---------|------------|----------------|",
      "   | [<canvas title>](<relative path to business-model.md>) | <segment> | <customer / competitor / vendor / partner> | <SMB / Mid-Market / Enterprise or `n/a`> |",
      "",
      "   ### Segment Classification",
      "",
      "   - **Primary segment:** [<segment name>](<relative path to segment index>) — <one sentence on fit>",
      "   - **Secondary segment(s):** <list or `n/a`>",
      "",
      "   ### Size Classification",
      "",
      "   Based on <headcount>, <revenue/funding>, and <any other size",
      "   signal>, this company matches the **<SMB / Mid-Market /",
      "   Enterprise>** variation in the <segment> business model.",
      "   ```",
      "",
      "   Use `_No matched business models in <BUSINESS_MODELS_ROOT>._` as",
      "   the table body when Step 3 found candidates but Step 4 scored",
      "   zero matches.",
      "",
      "6. **Update the matched business-model documents bidirectionally.**",
      "   For every row in the matched-models table, append a cross-link",
      "   to the canvas's own Traceability (or Sources) section — do not",
      "   rewrite the canvas body. A single line is enough:",
      "",
      "   ```markdown",
      "   - **Profiled company:** [<company name>](<relative path to profile.md>) — match type: <customer / competitor / vendor / partner>",
      "   ```",
      "",
      "   If a canvas already lists this profile, do not duplicate the line.",
      "   If the canvas has no Traceability or Sources section, skip the",
      "   canvas and flag it in the profile's `## Risks / Open Questions`",
      "   section rather than inventing a new section in someone else's",
      "   document.",
      "",
      "7. **Update the profile's `referencedIn` frontmatter.** Add the",
      "   relative paths of every matched business-model document and",
      "   segment index so downstream audit tooling can traverse the link",
      "   graph without re-scanning the tree.",
      "",
      "8. **Append a revision-history row.**",
      "",
      "   ```markdown",
      "   | YYYY-MM-DD | Matched to business models: <comma-separated segment list> |",
      "   ```",
      "",
      "9. **Commit and push.** Report the matched segments on the match",
      "   issue with a short comment, and close it",
      `   ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 5: Refresh (`company:refresh`)",
      "",
      "**Goal:** Re-verify an existing profile with a small number of",
      "targeted web searches for recent developments, then update the",
      "profile in place.",
      "",
      "**Budget:** 4–6 targeted web searches, focused on recent activity.",
      "Do not redo the full research-phase sweep.",
      "",
      "### Steps",
      "",
      "1. **Read the existing profile** at the path referenced in the issue",
      "   body. If the profile file is missing, close the issue with",
      "   `status:needs-attention` and stop.",
      "",
      "2. **Confirm the staleness threshold.** Compare the profile's `date`",
      "   frontmatter against today's date and the `refresh_days`",
      "   frontmatter (or the project default from the Refresh Cadence",
      "   section above). If the profile is younger than the threshold and",
      "   the issue body does not set `force: true`, close the issue with",
      "   a short comment and stop — do not burn the search budget.",
      "",
      "3. **Grep for `as of ` qualifiers.** Per the",
      "   `temporal-framing-convention` rule, every time-sensitive claim in",
      "   the profile carries an inline `as of [YYYY-MM-DD]` or `as of",
      "   [Month YYYY]` qualifier. Build the list of qualified claims and",
      "   compute the age (in days) of each one against today's date.",
      "   Classify each claim against the cadence table in the",
      "   `temporal-framing-convention` rule:",
      "   - **company-leadership** — 90 days",
      "   - **regulatory-status** — 30 days",
      "   - **litigation** — 30 days",
      "   - **ownership** — 180 days",
      "   - **dated-metrics** — 180 days",
      "",
      "   Treat every qualifier older than its category's cadence as a",
      "   re-verification target for the targeted-search step below. If the",
      "   profile contains **no** `as of ` qualifiers at all, treat the",
      "   entire profile as needing **back-fill** — apply the convention to",
      "   every time-sensitive claim before running the targeted searches.",
      "",
      "4. **Run 4–6 targeted searches.** Focus on the categories surfaced by",
      "   the qualifier grep (step 3), plus any additional signals worth a",
      "   refresh:",
      "   - `<company name>` + recent news, press releases, or blog posts",
      "   - Product launches, feature changes, or pricing updates",
      "   - Funding rounds, acquisitions, or material ownership changes",
      "   - Leadership changes (CEO, CTO, CPO, notable departures/arrivals)",
      "   - New partnerships, certifications, or regulatory milestones",
      "",
      "5. **Update the profile in place.** Edit the affected sections with",
      "   the new information. Cite every new claim. Preserve the slug and",
      "   the original `parent_issue` field. Bump the `date` frontmatter to",
      "   today's date. **Update the `as of` qualifier** on every claim you",
      "   re-verified — change the date to today's date when the claim",
      "   still holds; rewrite the framing when the claim has changed.",
      "   Surface unverifiable or stale-and-changed claims through",
      "   `## Risks / Open Questions` rather than silently dropping them.",
      "",
      "6. **Do not silently re-type the company.** If the refresh surfaces",
      "   evidence that the company's primary type has changed (e.g., an",
      "   industry-player that now sells a directly competing product),",
      "   flag it in `## Risks / Open Questions` and stop — do **not**",
      "   rewrite the `company_type` frontmatter without an explicit",
      "   override in the refresh issue body (`retype: <new type>`).",
      "",
      "7. **Append a revision-history row.** Summarize the delta in one",
      "   line:",
      "",
      "   ```markdown",
      "   | YYYY-MM-DD | Refreshed: <one-line summary of what changed> |",
      "   ```",
      "",
      "   When the search budget finds no material change, the row still",
      "   appears so the next scheduled refresh knows this profile was",
      "   reviewed:",
      "",
      "   ```markdown",
      "   | YYYY-MM-DD | Refreshed: no material change |",
      "   ```",
      "",
      "8. **Update reference entries** (if the project tracks company",
      "   references under research directories). If the company's",
      "   relevance to a research area has shifted — for example, it is no",
      "   longer a live competitor, or it now plays in a new segment —",
      "   update the corresponding entry under `referencedIn` and the",
      "   research-area doc that links to this profile.",
      "",
      "9. **Commit and push.** Report the delta on the refresh issue with",
      "   a short comment (or `no material change`), and close it",
      `   ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 6: Analyze (`company:analyze`)",
      "",
      "**Goal:** Produce a segment-level competitive analysis by",
      "synthesizing every profile in the target segment. The deliverable",
      "is one markdown document under `<ANALYSIS_DIR>` combining a",
      "feature-comparison matrix, a positioning map, and the strategic",
      "implications for the consuming project.",
      "",
      "**Budget:** No web searches. Pure synthesis from existing profiles",
      "and (optionally) existing product docs the consuming project",
      "maintains.",
      "",
      "### Steps",
      "",
      "1. **Read the segment scope from the issue body.** It must carry",
      "   `<SEGMENT_SLUG>` plus the path of every profile to include (or a",
      "   glob that resolves to them). If the issue body is ambiguous,",
      "   close with `status:needs-attention` and stop.",
      "",
      "2. **Block on open draft issues.** Query for open",
      "   `company:draft` issues whose title or body mentions the segment",
      "   slug:",
      "",
      "   ```bash",
      "   gh issue list \\",
      "     --label 'company:draft' --state open \\",
      "     --search '<SEGMENT_SLUG> in:title,body' \\",
      "     --json number,title --limit 200",
      "   ```",
      "",
      "   If the query returns any issues, this analyze issue must block on",
      "   them. Add `Depends on: #<N>` lines for every open draft, apply",
      "   `status:blocked`, remove `status:in-progress`, and stop — **do",
      "   not publish a partial landscape**. The issue will be eligible",
      "   again once the drafts close.",
      "",
      "3. **Read every in-scope profile.** Collect each profile's Summary,",
      "   Offering, Classification, Market Position, Positioning /",
      "   Differentiation, and Risks / Open Questions sections.",
      "",
      "4. **Derive a feature axis.** Build the feature axis from the",
      "   union of bullets across each profile's Offering section (and, if",
      "   present, a project-maintained feature taxonomy under",
      "   `<BUSINESS_MODELS_ROOT>`). Do **not** invent features not",
      "   represented in at least one profile or the project's taxonomy.",
      "",
      "5. **Write the analysis document** to",
      "   `<ANALYSIS_DIR>/<SEGMENT_SLUG>.md` using the template below. Use",
      "   `_Not available in profiles._` in any cell a profile does not",
      "   document. Never fabricate a `Yes/No` from a missing field.",
      "",
      "   ```markdown",
      "   ---",
      '   title: "<segment name> Competitive Analysis"',
      "   slug: <SEGMENT_SLUG>",
      "   segment: <segment name>",
      "   date: YYYY-MM-DD",
      "   status: draft",
      "   profiles:",
      "     - <relative path to profile 1>",
      "     - <relative path to profile 2>",
      "   ---",
      "",
      "   ## Market Overview",
      "   <landscape summary — total players, market structure, trends",
      "    visible from the profiles>",
      "",
      "   ## Feature Comparison Matrix",
      "   | Feature | <Company A> | <Company B> | <Company C> |",
      "   |---------|-------------|-------------|-------------|",
      "   | <feature> | Yes / No / Partial | ... | ... |",
      "",
      "   ## Pricing Comparison",
      "   <pricing tiers across profiles, where disclosed>",
      "",
      "   ## Positioning Map",
      "   <where each player sits — by price vs. feature depth, or by",
      "    segment focus. Textual description; link to an external diagram",
      "    if the consuming project maintains one.>",
      "",
      "   ## Strengths & Vulnerabilities",
      "   | Company | Key Strength | Key Vulnerability |",
      "   |---------|--------------|-------------------|",
      "   | <company> | <strength> | <vulnerability> |",
      "",
      "   ## Strategic Implications",
      "   <which features matter most, where the consuming project can",
      "    differentiate, what pricing the landscape supports, and which",
      "    competitive threats to watch>",
      "",
      "   ## Recommended Actions",
      "   | Action | Priority | Rationale |",
      "   |--------|----------|-----------|",
      "   | <action> | High / Medium / Low | <why> |",
      "",
      "   ## Sources",
      "   - <relative path to profile 1>",
      "   - <relative path to profile 2>",
      "",
      "   ## Revision History",
      "   | Date | Changes |",
      "   |------|---------|",
      "   | YYYY-MM-DD | Initial analysis |",
      "   ```",
      "",
      "6. **Do not edit the underlying profiles.** `company:analyze` is a",
      "   synthesis phase — profiles are **inputs only**. Any cross-link",
      "   from a profile to the analysis document should be added by a",
      "   follow-up `company:match` pass, not by this phase.",
      "",
      "7. **Commit and push.** Name the analysis document path on the",
      "   analyze issue with a short comment, and close it",
      `   ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Output Boundaries",
      "",
      "This agent writes **only** to:",
      "",
      "- `<NOTES_DIR>/` — research-notes files (Phase 1)",
      "- `<PROFILES_DIR>/` — company profiles (Phase 2, updated in Phases",
      "  3, 4, and 5)",
      "- `<ANALYSIS_DIR>/` — segment-level competitive-analysis documents",
      "  (Phase 6)",
      "",
      "In Phase 3, this agent also **creates `people:research` and",
      "`software:research` issues** for people and software products",
      "surfaced in the profile. It never writes the downstream profiles",
      "themselves — those are the responsibility of the",
      "`people-profile-analyst` and `software-profile-analyst` agents, which",
      "pick up the issues this pipeline creates.",
      "",
      "In Phase 4, this agent appends a single bidirectional cross-link",
      "line to matched business-model documents under",
      "`<BUSINESS_MODELS_ROOT>`. It never rewrites the canvas body, adds",
      "new canvas sections, or otherwise mutates business-model content —",
      "that remains the responsibility of the `business-models` bundle.",
      "",
      "In Phase 5, this agent only updates an existing profile in place —",
      "it never forks a new profile under a different slug.",
      "",
      "In Phase 6, this agent only writes a new segment-level analysis",
      "document. It never edits the underlying company profiles.",
      "",
      "The pipeline produces **company profiles, notes, and segment",
      "analyses**. Deeper research on people or products is delegated to",
      "downstream research pipelines via `people:research` and",
      "`software:research` issues — this agent never writes person",
      "profiles, software profiles, product evaluations, or",
      "requirement-style documents itself. Keep this boundary clean so",
      "the company-profile pipeline stays generic.",
      "",
      "---",
      "",
      "## Rules",
      "",
      "- **One company per session.** Never profile two companies back-to-back.",
      "  `company:analyze` is the single exception — it synthesizes one",
      "  segment per session, not one company.",
      "- **Persist before closing.** Every phase must write its output file",
      "  before closing its issue.",
      "- **Cite everything.** Profile claims without source citations do not",
      "  belong in the deliverable.",
      "- **No assumed competition.** Never call a company a competitor unless",
      "  the invoking issue body or the consuming project's configuration",
      "  says so.",
      "- **Delegate, don't duplicate.** People and software products",
      "  surfaced during profiling are handed off to the `people-profile`",
      "  and `software-profile` bundles via `people:research` and",
      "  `software:research` issues in Phase 3 — never inlined as person or",
      "  software profiles in this pipeline.",
      "- **Check before enqueueing.** Before opening a `people:research` or",
      "  `software:research` issue, verify no existing profile or open",
      "  research issue already covers the candidate. Reuse existing",
      "  profiles as cross-references rather than re-queuing research.",
      "- **Restrain the queue.** Only enqueue downstream research for people",
      "  and products that are genuinely relevant to the framing of this",
      "  profile. Do not open issues for every name or product mentioned in",
      "  passing.",
      "- **Produce profiles and analyses, not requirement documents.** Do",
      "  not open `type:requirement` or formal evaluation issues from this",
      "  pipeline. Follow-up research is scoped through `people:research`",
      "  and `software:research` only.",
      "- **Match is read-mostly.** Phase 4 edits the target profile and",
      "  appends one line to each matched business-model document. It never",
      "  rewrites canvas bodies, never runs web searches, and never edits",
      "  other profiles.",
      "- **Refresh respects the cadence.** Phase 5 exits early when the",
      "  profile is younger than the staleness threshold and `force: true`",
      "  is not set. It always appends a revision-history row so the next",
      "  scheduled run knows the profile was reviewed.",
      "- **Analyze blocks on open drafts.** Phase 6 refuses to publish a",
      "  partial landscape. If any `company:draft` issues for the target",
      "  segment are still open, the analyze issue is marked",
      "  `status:blocked` with `Depends on:` lines pointing at each draft.",
      "- **Refresh, don't fork.** When a profile exists and is past its",
      "  cadence, update in place rather than creating a new slug.",
    ].join("\n"),
  };
}

/**
 * Regression eval suite shipped alongside the `profile-company` skill
 * at `.claude/skills/profile-company/evals/evals.json`. Each entry
 * exercises a distinct branch of the pipeline:
 *
 * - A fresh profile from a company name that triggers type inference
 *   and `people:research` / `software:research` hand-offs.
 * - A profile that must cite its source tiers correctly (T1/T2/T3/T4)
 *   because one claim conflicts across sources.
 * - A follow-up cycle where the profile already exists and the user
 *   expects refresh semantics, not a fork.
 *
 * Suites follow the skill-evals schema — see the `skill-evals` rule
 * content rendered by `./skill-evals.ts#renderSkillEvalsRuleContent`.
 */
function buildProfileCompanyEvalsJson(
  issueDefaults: ResolvedIssueDefaults,
): string {
  const research = labelsForPhase(issueDefaults, "company:research");
  return JSON.stringify(
    {
      skill_name: "profile-company",
      evals: [
        {
          id: 1,
          prompt:
            "/profile-company Acme Registration — they're a mid-market B2B event platform we ran into at a trade show last month. framing: competitive analysis for our self-service registration feature.",
          expected_output: `A \`company:research\` issue is created with \`type:company-profile\`, \`priority:${research.priority}\`, and \`status:${research.status}\`. The issue body carries the company name, an inferred or stated type (likely \`competitor\` given the framing), the framing text, and any supplied overrides. Phase 1 produces a research-notes file under the project's notes directory with sources annotated by tier (T1/T2/T3/T4) and date. Phase 2 opens a \`company:draft\` issue and may additionally open \`people:research\` issues for notable leaders surfaced and \`software:research\` issues for adjacent product mentions. The skill does not author person profiles, software profiles, or comparative analyses itself.`,
          files: [],
          product_context_refs: [
            "Mission",
            "Domain Vocabulary",
            "In-Scope Capabilities",
          ],
        },
        {
          id: 2,
          prompt:
            "Profile a startup called Lumen Events. Their current company page lists a 150-person team but a TechCrunch article from six months ago said they were at 80 people. Handle the conflict.",
          expected_output:
            "The `company:research` issue body captures the conflict up-front under a dedicated conflict-resolution note. The research notes file annotates the live company page as a T1 living source and the TechCrunch article as a T3 secondary source. Conflict resolution follows the documented hierarchy: T1 wins on headcount (a fast-decay claim), so the current-state profile records 150 employees. The profile adds an inline note flagging that the T3 source is six months old and may simply be stale. Sources section lists each citation with tier, access date or publish date, and URL.",
          files: [],
          product_context_refs: ["Domain Vocabulary"],
        },
        {
          id: 3,
          prompt:
            "/profile-company Acme Registration — we already have a profile for them but it's nine months old. Refresh it.",
          expected_output:
            "The pipeline routes through the refresh phase (not a fresh profile). It reads the existing profile slug and refuses to create a second profile under a new slug. When the existing profile is younger than the configured staleness threshold and `force: true` is not set, the refresh phase exits early and appends a revision-history row noting the review. When the profile is past cadence (nine months qualifies for most fast-decay fields), the refresh updates fast-decay claims in place (headcount, leadership, pricing, product features) while preserving stable history, and appends a revision-history row citing what changed and which sources were re-checked. No new profile file is created.",
          files: [],
          product_context_refs: ["Domain Vocabulary"],
        },
      ],
    },
    null,
    2,
  );
}

/**
 * Reference files emitted alongside the `profile-company` SKILL.md.
 * Currently ships the eval suite only — additional reference assets
 * (profile templates, notes stubs) land here as the bundle grows.
 */
function buildProfileCompanyReferenceFiles(
  issueDefaults: ResolvedIssueDefaults,
): ReadonlyArray<{ readonly path: string; readonly content: string }> {
  return [
    {
      path: "evals/evals.json",
      content: buildProfileCompanyEvalsJson(issueDefaults),
    },
  ];
}

/** Skill that kicks off a company profile cycle. */
function buildProfileCompanySkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "profile-company",
    description:
      "Kick off a company-profile pipeline. Creates a company:research issue for the given company and dispatches Phase 1 (Research) in the company-profile-analyst agent.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "company-profile-analyst",
    platforms: { cursor: { exclude: true } },
    referenceFiles: buildProfileCompanyReferenceFiles(issueDefaults),
    instructions: [
      "# Profile Company",
      "",
      "Kick off a company-profile pipeline. Creates a `company:research`",
      "issue carrying the company name, type, and framing, then dispatches",
      "Phase 1 (Research) in the company-profile-analyst agent.",
      "",
      "## Usage",
      "",
      "/profile-company <company-name>",
      "",
      "Optional extensions in the issue body:",
      "- `type: <competitor | industry-player | software-vendor | customer |",
      "   partner | investor>` — override the default type inference",
      "- `website: <url>` — canonical website if not obvious from the name",
      "- `framing: <text>` — why this profile was requested and what to learn",
      "- `slug: <kebab-case>` — override the derived company slug",
      "- `sources: <list>` — additional authorized sources beyond public web",
      "",
      "## Default Paths",
      "",
      `If the project has no override in \`${paths.docsRoot}/project-context.md\` or`,
      "`agentConfig.rules`, outputs land under:",
      "",
      `- \`${paths.companiesRoot}/notes/<slug>.notes.md\``,
      `- \`${paths.companiesRoot}/profiles/<slug>.md\``,
      "",
      "## Steps",
      "",
      "1. Create a `company:research` issue with `type:company-profile`,",
      `   \`priority:${labelsForPhase(issueDefaults, "company:research").priority}\`, and \`status:${labelsForPhase(issueDefaults, "company:research").status}\`. Body must include the`,
      "   company name, selected type, framing, and any overrides.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 1 (Research) of the company-profile-analyst agent.",
      "3. Phase 1 creates the `company:draft` issue. Phase 2 may create a",
      "   `company:followup` issue **and** a `company:match` issue. Each",
      "   downstream issue declares its `Depends on:` predecessor.",
      "",
      "## Output",
      "",
      "- A research-notes file under the project's notes directory",
      "- A single company profile under the profiles directory",
      "- `people:research` issues for notable people surfaced in the profile",
      "  (handed off to the `people-profile` bundle)",
      "- `software:research` issues for software products surfaced in the",
      "  profile (handed off to the `software-profile` bundle)",
      "- A `company:match` issue so the profile is enriched against the",
      "  project's business-model canvases",
      "- This pipeline produces **company profiles only** — it does not",
      "  write person profiles, software profiles, product evaluations, or",
      "  comparative analyses itself. Segment-level analysis is produced by",
      "  the separate `/analyze-segment` skill.",
    ].join("\n"),
  };
}

/** Skill that kicks off a company match (enrichment) cycle. */
function buildMatchCompanySkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "match-company",
    description:
      "Kick off a company-profile match (enrichment) cycle. Creates a company:match issue for an existing profile and dispatches Phase 4 (Match) in the company-profile-analyst agent. Match enriches the profile with business-model, segment, and size context from existing docs — no web searches.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "company-profile-analyst",
    platforms: { cursor: { exclude: true } },
    instructions: [
      "# Match Company",
      "",
      "Enrich an existing company profile against the project's",
      "business-model canvases, industry segments, and size descriptions.",
      "Creates a `company:match` issue for the target profile and",
      "dispatches Phase 4 (Match) in the company-profile-analyst agent.",
      "",
      "**This is enrichment of a known profile, not entity",
      "deduplication** — the profile already exists and must be passed by",
      "path.",
      "",
      "## Usage",
      "",
      "/match-company <path-to-profile>",
      "",
      "Optional extensions in the issue body:",
      "- `business_models_root: <path>` — override `<BUSINESS_MODELS_ROOT>`",
      "  if the project's business-model canvases do not live at the",
      "  default location",
      "- `only: <comma-separated list of segment slugs>` — match only",
      "  against the listed segments (useful when a new canvas triggers a",
      "  re-match)",
      "",
      "## Default Paths",
      "",
      `If the project has no override in \`${paths.docsRoot}/project-context.md\` or`,
      "`agentConfig.rules`, the agent reads canvases from:",
      "",
      `- \`${paths.docsRoot}/industry-research/\``,
      "",
      "## Budget",
      "",
      "- **No web searches.** Phase 4 reads existing docs and edits the",
      "  profile + one cross-link per matched canvas. Anything that cannot",
      "  be decided from existing docs is flagged in the profile's",
      "  `## Risks / Open Questions` section.",
      "",
      "## Steps",
      "",
      "1. Create a `company:match` issue with `type:company-profile`,",
      `   \`priority:${labelsForPhase(issueDefaults, "company:match").priority}\`, and \`status:${labelsForPhase(issueDefaults, "company:match").status}\`. Body must include the`,
      "   profile path and any overrides.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 4 (Match) of the company-profile-analyst agent.",
      "",
      "## Output",
      "",
      "- The profile gains a `## Business Model Context` section",
      "- Each matched business-model canvas gains a single cross-link line",
      "  in its Traceability / Sources section",
      "- The profile's `referencedIn` frontmatter is updated with matched",
      "  paths",
      "- A revision-history row summarizing the matched segments",
    ].join("\n"),
  };
}

/** Skill that kicks off a company refresh (maintenance) cycle. */
function buildRefreshCompanySkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "refresh-company",
    description:
      "Kick off a company-profile refresh cycle. Creates a company:refresh issue for an existing profile and dispatches Phase 5 (Refresh) in the company-profile-analyst agent. Refresh re-verifies the profile with 4–6 targeted web searches and updates it in place. Respects a configurable staleness threshold so profiles younger than the threshold exit early.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "company-profile-analyst",
    platforms: { cursor: { exclude: true } },
    instructions: [
      "# Refresh Company",
      "",
      "Re-verify an existing company profile with a small number of",
      "targeted web searches and update the profile in place. Creates a",
      "`company:refresh` issue for the target profile and dispatches",
      "Phase 5 (Refresh) in the company-profile-analyst agent.",
      "",
      "Refresh respects a configurable **staleness threshold** so scheduled",
      "refreshes do not burn search budget on profiles that are still",
      "fresh.",
      "",
      "## Usage",
      "",
      "/refresh-company <path-to-profile>",
      "",
      "Optional extensions in the issue body:",
      "- `refresh_days: <N>` — override the default 180-day staleness",
      "  threshold for this run",
      "- `force: true` — refresh even if the profile is younger than the",
      "  threshold",
      "- `retype: <taxonomy value>` — explicitly allow Phase 5 to change",
      "  the profile's `company_type` when the refresh finds material",
      "  evidence of a type change",
      "",
      "## Staleness Threshold",
      "",
      "Order of precedence (first match wins):",
      "",
      "1. `refresh_days` in the issue body (per-run override)",
      "2. `refresh_days` frontmatter on the profile itself",
      "3. Project-wide default declared under a `## Refresh Cadence`",
      `   section in \`${paths.docsRoot}/project-context.md\``,
      "4. Built-in default: **180 days**",
      "",
      "If the profile's `date` frontmatter plus the resolved threshold is",
      "in the future (i.e. the profile is still fresh) and `force: true`",
      "is not set, Phase 5 closes the refresh issue with a short comment",
      "and stops without running any searches.",
      "",
      "## Budget",
      "",
      "- **4–6 targeted web searches** focused on recent news, product",
      "  launches, funding/acquisitions, leadership changes, and",
      "  partnerships / certifications.",
      "",
      "## Steps",
      "",
      "1. Create a `company:refresh` issue with `type:company-profile`,",
      `   \`priority:${labelsForPhase(issueDefaults, "company:refresh").priority}\`, and \`status:${labelsForPhase(issueDefaults, "company:refresh").status}\`. Body must include the`,
      "   profile path and any overrides.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 5 (Refresh) of the company-profile-analyst agent.",
      "",
      "## Output",
      "",
      "- The profile is updated in place (sections edited, `date` bumped,",
      "  slug preserved)",
      "- A revision-history row summarizing the delta — or",
      "  `Refreshed: no material change` when the search budget found",
      "  nothing material",
      "- Reference entries updated if the company's relevance to a research",
      "  area shifted",
    ].join("\n"),
  };
}

/** Skill that kicks off a segment-level competitive analysis cycle. */
function buildAnalyzeSegmentSkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "analyze-segment",
    description:
      "Kick off a segment-level competitive-analysis cycle. Creates a company:analyze issue for the target segment and dispatches Phase 6 (Analyze) in the company-profile-analyst agent. Analyze synthesizes every profile in the segment into one competitive-analysis document. Blocks on open company:draft issues for the segment.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "company-profile-analyst",
    platforms: { cursor: { exclude: true } },
    instructions: [
      "# Analyze Segment",
      "",
      "Produce a segment-level competitive analysis by synthesizing every",
      "company profile in the target segment into one markdown document.",
      "Creates a `company:analyze` issue and dispatches Phase 6 (Analyze)",
      "in the company-profile-analyst agent.",
      "",
      "**This is pure synthesis** — no web searches, no new profile work.",
      "All inputs must already exist under the profiles directory before",
      "you run this skill.",
      "",
      "## Usage",
      "",
      "/analyze-segment <segment-slug>",
      "",
      "Optional extensions in the issue body:",
      "- `profiles: <list of paths>` — explicit list of profile files to",
      "  include; otherwise the agent discovers profiles by scanning for",
      "  the segment slug in each profile's `referencedIn` frontmatter",
      "- `analysis_dir: <path>` — override `<ANALYSIS_DIR>` if the project",
      "  stores analyses somewhere other than the default",
      "",
      "## Default Paths",
      "",
      `If the project has no override in \`${paths.docsRoot}/project-context.md\` or`,
      "`agentConfig.rules`, outputs land under:",
      "",
      `- \`${paths.companiesRoot}/analysis/<segment-slug>.md\``,
      "",
      "## Blocking Rule",
      "",
      "Phase 6 refuses to publish a partial landscape. If any",
      "`company:draft` issues whose title or body mention",
      "`<SEGMENT_SLUG>` are still open, the analyze issue is marked",
      "`status:blocked` with `Depends on: #<N>` lines pointing at each",
      "open draft, `status:in-progress` is removed, and Phase 6 exits",
      "without writing the analysis. The issue becomes eligible again",
      "once the drafts close.",
      "",
      "## Budget",
      "",
      "- **No web searches.** Pure synthesis from existing profiles (and",
      "  optionally the project's own product docs).",
      "",
      "## Steps",
      "",
      "1. Create a `company:analyze` issue with `type:company-profile`,",
      `   \`priority:${labelsForPhase(issueDefaults, "company:analyze").priority}\`, and \`status:${labelsForPhase(issueDefaults, "company:analyze").status}\`. Body must include the`,
      "   segment slug and any overrides.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 6 (Analyze) of the company-profile-analyst agent.",
      "",
      "## Output",
      "",
      "- One markdown document at `<ANALYSIS_DIR>/<SEGMENT_SLUG>.md`",
      "  combining feature-comparison matrix, pricing comparison,",
      "  positioning map, strengths & vulnerabilities, strategic",
      "  implications, and recommended actions — every claim sourced back",
      "  to the underlying profiles",
      "- This skill does **not** edit the underlying profiles. Cross-",
      "  linking a profile back to the analysis is the job of a",
      "  subsequent `company:match` pass.",
    ].join("\n"),
  };
}

/**
 * Build the company-profile bundle with the supplied resolved paths.
 *
 * Every reference to a canonical agent path (docs root, etc.) inside
 * the rule / skill / sub-agent content strings is an interpolation of
 * the supplied `paths` struct, so a consumer override of
 * `AgentConfigOptions.paths` propagates to the rendered output.
 *
 * Ships a sub-agent (`company-profile-analyst`), four user-invocable
 * skills (`/profile-company`, `/match-company`, `/refresh-company`,
 * `/analyze-segment`), and `type:company-profile` plus `company:*`
 * phase labels for the six phases.
 */
export function buildCompanyProfileBundle(
  paths: ResolvedAgentPaths = DEFAULT_AGENT_PATHS,
  issueDefaults: ResolvedIssueDefaults = DEFAULT_RESOLVED_ISSUE_DEFAULTS,
  tier: AgentModel = AGENT_MODEL.BALANCED,
): AgentRuleBundle {
  return {
    name: "company-profile",
    description:
      "Company research and profiling pipeline: research, draft profile, followup, match, refresh, analyze. Enabled by default; domain-neutral; filesystem-durable between phases. Phase 3 (Followup) hands surfaced people and software products off to the `people-profile` and `software-profile` bundles via `people:research` and `software:research` issues. Phase 4 (Match) enriches profiles against business-model canvases. Phase 5 (Refresh) re-verifies profiles on a configurable staleness cadence. Phase 6 (Analyze) synthesizes profiles in a segment into a competitive-analysis document.",
    appliesWhen: () => true,
    rules: [
      {
        name: "company-profile-workflow",
        description:
          "Describes the 6-phase company-profile pipeline, the company:* label taxonomy, and the boundary against downstream research agents.",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Company Profile Workflow",
          "",
          "Use `/profile-company <company-name>` to kick off a company",
          "research and profiling pipeline. The pipeline runs in up to 6",
          "phases — research, draft, followup, match, refresh, analyze —",
          "each tracked by its own GitHub issue labeled `company:research`,",
          "`company:draft`, `company:followup`, `company:match`,",
          "`company:refresh`, or `company:analyze`. All issues carry",
          "`type:company-profile`.",
          "",
          "Three additional user-invocable skills drive the maintenance and",
          "synthesis phases independently:",
          "",
          "- `/match-company <path-to-profile>` — enrich an existing",
          "  profile with business-model and segment context from the",
          "  project's business-model canvases (Phase 4, no web searches).",
          "- `/refresh-company <path-to-profile>` — re-verify an existing",
          "  profile with 4–6 targeted web searches and update it in place",
          "  (Phase 5, respects a configurable staleness threshold).",
          "- `/analyze-segment <segment-slug>` — synthesize every profile",
          "  in a segment into one competitive-analysis document (Phase 6,",
          "  no web searches; blocks on open `company:draft` issues for",
          "  the segment).",
          "",
          "The pipeline produces **company profiles, notes, and segment",
          "analyses**. Deeper research on notable people and software",
          "products surfaced while profiling is delegated to the",
          "`people-profile` and `software-profile` bundles via",
          "`people:research` and `software:research` issues opened during",
          "Phase 3 (Followup).",
          "",
          "See the `company-profile-analyst` agent definition for full",
          "workflow details, default paths, the company-type taxonomy, the",
          "staleness-threshold rules, and phase-by-phase instructions.",
        ].join("\n"),
        platforms: {
          cursor: { exclude: true },
        },
        tags: ["workflow"],
      },
    ],
    skills: [
      buildProfileCompanySkill(paths, issueDefaults),
      buildMatchCompanySkill(paths, issueDefaults),
      buildRefreshCompanySkill(paths, issueDefaults),
      buildAnalyzeSegmentSkill(paths, issueDefaults),
    ],
    subAgents: [buildCompanyProfileAnalystSubAgent(paths, issueDefaults, tier)],
    labels: [
      {
        name: "type:company-profile",
        color: "0E8A16",
        description:
          "Work that produces or maintains a company profile or its research notes",
      },
      {
        name: "company:research",
        color: "C5DEF5",
        description:
          "Phase 1: gather public sources about a company into a research-notes file",
      },
      {
        name: "company:draft",
        color: "BFDADC",
        description:
          "Phase 2: write the structured company profile from research notes",
      },
      {
        name: "company:followup",
        color: "D4C5F9",
        description:
          "Phase 3: enqueue follow-up research issues for people and products surfaced in the profile",
      },
      {
        name: "company:match",
        color: "FBCA04",
        description:
          "Phase 4: enrich a company profile with business-model, segment, and size context from existing docs",
      },
      {
        name: "company:refresh",
        color: "F9D0C4",
        description:
          "Phase 5: re-verify an existing company profile with targeted web searches and update it in place",
      },
      {
        name: "company:analyze",
        color: "5319E7",
        description:
          "Phase 6: synthesize every company profile in a segment into a competitive-analysis document",
      },
    ],
  };
}

/**
 * Default-paths instance of the company-profile bundle, preserved for
 * backward compatibility with consumers that import the const
 * directly. The factory above is the canonical entry point when a
 * consumer supplies `AgentConfigOptions.paths`.
 */
export const companyProfileBundle: AgentRuleBundle =
  buildCompanyProfileBundle();
